import {
  BufferAttribute,
  BufferGeometry,
  Color,
  Group,
  Object3D,
  Points,
  PointsMaterial,
  Scene,
} from "three";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import type { Mat3, PoseFrame, Player } from "../data/types";
import { expectedBoneLengthsM } from "./skeletonPriors";

/** Flatten a row-major 3×3 into a length-9 Float32Array. Identity if undefined. */
function flattenRot(R: Mat3 | undefined): Float32Array {
  const out = new Float32Array(9);
  if (R) {
    out[0] = R[0][0]; out[1] = R[0][1]; out[2] = R[0][2];
    out[3] = R[1][0]; out[4] = R[1][1]; out[5] = R[1][2];
    out[6] = R[2][0]; out[7] = R[2][1]; out[8] = R[2][2];
  } else {
    out[0] = 1; out[4] = 1; out[8] = 1;
  }
  return out;
}

// Mirrors render_qa._COLORS so a given gid renders the same color across tools.
const PALETTE: ReadonlyArray<readonly [number, number, number]> = [
  [255, 56, 56], [56, 255, 56], [56, 56, 255], [255, 255, 56],
  [255, 56, 255], [56, 255, 255], [255, 153, 56], [153, 56, 255],
  [56, 255, 153], [255, 56, 153], [153, 255, 56], [56, 153, 255],
  [200, 200, 120], [120, 200, 200], [200, 120, 200], [180, 90, 90],
];

function paletteColor(gid: number): Color {
  const rgb = PALETTE[((gid % PALETTE.length) + PALETTE.length) % PALETTE.length];
  return new Color(rgb[0] / 255, rgb[1] / 255, rgb[2] / 255);
}

/** CSS rgb(...) string for a given gid, matching the 3D palette. */
export function paletteCss(gid: number): string {
  const rgb = PALETTE[((gid % PALETTE.length) + PALETTE.length) % PALETTE.length];
  return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
}

/** Holds geometry buffers for one player; reused across frames. */
class SkeletonObj {
  group: Group;
  joints: Points;
  jointGeom: BufferGeometry;
  bones: LineSegments2;
  boneGeom: LineSegmentsGeometry;
  boneMat: LineMaterial;
  /** Sized lazily on the first update() from the player's actual kpts length
   *  (26 for Halpe-26, 133 for COCO-WholeBody). Starts empty. */
  jointPositions = new Float32Array(0);
  bonePositions: Float32Array;
  edges: ReadonlyArray<[number, number]>;
  visible = false;

  jointMat: PointsMaterial;

  constructor(gid: number, edges: ReadonlyArray<[number, number]>,
              sharedRes: { w: number; h: number }, initLinewidthPx: number,
              initJointSize: number) {
    this.edges = edges;
    const color = paletteColor(gid);

    this.jointGeom = new BufferGeometry();
    this.jointGeom.setAttribute("position",
      new BufferAttribute(this.jointPositions, 3));
    const jointGeom = this.jointGeom;
    this.jointMat = new PointsMaterial({
      color, size: initJointSize, sizeAttenuation: true,
    });
    this.joints = new Points(jointGeom, this.jointMat);

    // Bones use Line2's screen-space-thickness shader so the width is
    // controllable in pixels (LineBasicMaterial.linewidth is silently
    // ignored in WebGL). LineSegmentsGeometry expects [x,y,z, x,y,z, ...]
    // of paired endpoints — same shape as our bonePositions buffer.
    this.bonePositions = new Float32Array(edges.length * 2 * 3);
    this.boneGeom = new LineSegmentsGeometry();
    this.boneGeom.setPositions(this.bonePositions);
    this.boneMat = new LineMaterial({
      color: color.getHex(),
      linewidth: initLinewidthPx,
      worldUnits: false,                  // pixel-space thickness
      resolution: { x: sharedRes.w, y: sharedRes.h } as any,
      dashed: false,
      alphaToCoverage: true,
    });
    this.boneMat.resolution.set(sharedRes.w, sharedRes.h);
    this.bones = new LineSegments2(this.boneGeom, this.boneMat);
    this.bones.computeLineDistances();

    this.group = new Group();
    this.group.name = `skel_${gid}`;
    this.group.add(this.joints);
    this.group.add(this.bones);
  }

  /** Write joint xyzs (rotated to Y-up + scaled to metres) + bone endpoints
   *  from one Player. Any bone whose length exceeds (expectedLengthM[e] *
   *  maxRatio) is collapsed to a zero-length segment so it renders
   *  invisibly. Joints stay drawn regardless.
   *  `upRotFlat` is the row-major 9-element up-rotation matrix; identity
   *  is fine. */
  update(
    p: Player,
    metresPerWu: number,
    upRotFlat: Float32Array,
    expectedM: Float32Array,
    maxRatio: number,
  ): void {
    const s = metresPerWu;
    const r = upRotFlat;
    const numKpts = (p.kpts.length / 3) | 0;
    // Grow + rebind the joint buffer the first time we see this player's true
    // joint count (the constructor can't know it: 26 vs 133 is data-driven).
    if (this.jointPositions.length < numKpts * 3) {
      this.jointPositions = new Float32Array(numKpts * 3);
      this.jointGeom.setAttribute("position",
        new BufferAttribute(this.jointPositions, 3));
    }
    for (let j = 0; j < numKpts; j++) {
      const x = p.kpts[j * 3];
      const y = p.kpts[j * 3 + 1];
      const z = p.kpts[j * 3 + 2];
      // y_up = R · v  (rotation is length-preserving so bone gate stays valid)
      this.jointPositions[j * 3]     = (r[0] * x + r[1] * y + r[2] * z) * s;
      this.jointPositions[j * 3 + 1] = (r[3] * x + r[4] * y + r[5] * z) * s;
      this.jointPositions[j * 3 + 2] = (r[6] * x + r[7] * y + r[8] * z) * s;
    }
    (this.joints.geometry.attributes.position as BufferAttribute).needsUpdate = true;
    this.joints.geometry.computeBoundingSphere();

    for (let e = 0; e < this.edges.length; e++) {
      const [a, b] = this.edges[e];
      const ax = this.jointPositions[a * 3];
      const ay = this.jointPositions[a * 3 + 1];
      const az = this.jointPositions[a * 3 + 2];
      const bx = this.jointPositions[b * 3];
      const by = this.jointPositions[b * 3 + 1];
      const bz = this.jointPositions[b * 3 + 2];
      const dx = bx - ax, dy = by - ay, dz = bz - az;
      const maxLen = expectedM[e] * maxRatio;
      const long = dx * dx + dy * dy + dz * dz > maxLen * maxLen;
      // Collapse over-long bones to their midpoint -> zero-length segment.
      // Mirrors render_qa's bone gating in render style.
      if (long) {
        const mx = (ax + bx) * 0.5, my = (ay + by) * 0.5, mz = (az + bz) * 0.5;
        this.bonePositions[e * 6]     = mx;
        this.bonePositions[e * 6 + 1] = my;
        this.bonePositions[e * 6 + 2] = mz;
        this.bonePositions[e * 6 + 3] = mx;
        this.bonePositions[e * 6 + 4] = my;
        this.bonePositions[e * 6 + 5] = mz;
      } else {
        this.bonePositions[e * 6]     = ax;
        this.bonePositions[e * 6 + 1] = ay;
        this.bonePositions[e * 6 + 2] = az;
        this.bonePositions[e * 6 + 3] = bx;
        this.bonePositions[e * 6 + 4] = by;
        this.bonePositions[e * 6 + 5] = bz;
      }
    }
    // LineSegmentsGeometry stores the data in an InstancedInterleavedBuffer
    // shared between instanceStart/instanceEnd; flagging the shared buffer
    // dirty is enough to upload our in-place mutation of bonePositions.
    const sa = this.boneGeom.attributes.instanceStart as any;
    if (sa?.data) sa.data.needsUpdate = true;
    this.boneGeom.computeBoundingSphere();
  }

  setLinewidth(px: number): void {
    this.boneMat.linewidth = px;
  }

  /** Joint dot radius in metres (world units, size-attenuated). */
  setJointSize(m: number): void {
    this.jointMat.size = m;
  }

  setResolution(w: number, h: number): void {
    this.boneMat.resolution.set(w, h);
  }

  setVisible(v: boolean): void {
    if (v === this.visible) return;
    this.visible = v;
    this.group.visible = v;
  }
}

/**
 * Pool of per-gid skeleton groups attached to the given scene. New gids are
 * allocated lazily on first appearance. Use applyFrame() once per render tick.
 */
export class SkeletonGroups {
  private root: Object3D;
  private edges: ReadonlyArray<[number, number]>;
  private expectedM: Float32Array;
  private upRotFlat: Float32Array;
  private byGid = new Map<number, SkeletonObj>();
  private metresPerWu: number;
  /** Shared screen resolution + linewidth; mutated through setters. New
   *  skeletons created lazily inherit the current values. */
  private res = { w: 1, h: 1 };
  private linewidthPx = 1;
  /** Joint dot radius in metres. Smaller than the old 0.06 default so the
   *  dense COCO-WholeBody face/hand keypoints don't merge into a blob. */
  private jointSize = 0.02;
  /** Per-bone ratio threshold: bone is hidden if its length exceeds
   *  `expected_length_for_that_bone * maxBoneRatio`. Default 1.8× covers
   *  natural posture variation (limb stretched out is ~1.3-1.4× neutral)
   *  plus foreshortening/calibration slack, with a safety margin. The old
   *  1.6× was tight enough to clip visibly-correct torso/arm/neck bones in
   *  real frames; 1.8× keeps those attached while still gating gross errors.
   *  Mutable so the UI slider can dial it without rebuilding the scene. */
  maxBoneRatio = 1.8;
  /** Set of global-IDs to hide entirely (drawn nowhere regardless of frame
   *  contents). Mutable so the gid-toggle UI can flip entries in place. */
  disabledGids = new Set<number>();

  constructor(root: Scene | Object3D,
              edges: ReadonlyArray<[number, number]>,
              metresPerWu: number,
              numKeypoints: number,
              upRotation?: Mat3) {
    this.root = new Group();
    this.root.name = "skeletons";
    root.add(this.root);
    this.edges = edges;
    // numKeypoints selects the prior table (26 → Halpe-26 vs 133 → COCO-WB);
    // the two layouts use the same indices for different joints past 16, so the
    // wrong table silently mis-gates the torso/neck bones.
    this.expectedM = expectedBoneLengthsM(edges, numKeypoints);
    this.metresPerWu = metresPerWu;
    this.upRotFlat = flattenRot(upRotation);
  }

  applyFrame(frame: PoseFrame): void {
    // 1) hide every existing gid by default.
    for (const obj of this.byGid.values()) obj.setVisible(false);
    // 2) update + show those present this frame (unless user-disabled).
    for (const p of frame.players) {
      if (this.disabledGids.has(p.gid)) continue;
      let obj = this.byGid.get(p.gid);
      if (!obj) {
        obj = new SkeletonObj(p.gid, this.edges, this.res, this.linewidthPx,
                              this.jointSize);
        this.byGid.set(p.gid, obj);
        this.root.add(obj.group);
      }
      obj.update(p, this.metresPerWu, this.upRotFlat, this.expectedM, this.maxBoneRatio);
      obj.setVisible(true);
    }
  }

  /** Bone width in pixels (Line2 screen-space). */
  setLinewidth(px: number): void {
    this.linewidthPx = px;
    for (const obj of this.byGid.values()) obj.setLinewidth(px);
  }

  /** Joint dot radius in metres (world units, size-attenuated). */
  setJointSize(m: number): void {
    this.jointSize = m;
    for (const obj of this.byGid.values()) obj.setJointSize(m);
  }

  /** Update the renderer resolution so Line2's pixel-thickness shader stays
   *  correct after a window/letterbox resize. */
  setResolution(w: number, h: number): void {
    this.res.w = w; this.res.h = h;
    for (const obj of this.byGid.values()) obj.setResolution(w, h);
  }
}
